import {
  Badge,
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  Text,
  VStack
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import { ActionButton } from '../../../../components/ActionButton'
import { DetailField } from '../../../../components/DetailField'
import { EmptyCell } from '../../../../components/EmptyCell'
import { EmptyState } from '../../../../components/EmptyState'
import { LoadingBlock } from '../../../../components/Loading'
import { SectionTitle } from '../../../../components/SectionCard'
import { StatusBadge } from '../../../../components/StatusBadge'
import { formatCurrency, formatCurrencyOrFree, formatDateTime, formatPhone, useAppToast } from '../../../../helpers'
import { EventTabsService } from '../../../../services/Event/EventTabsService'
import { EventTabDetail, EventTabItem, EventTabMachine } from '../../../../services/Event/types'
import PaymentProof from './PaymentProof'
import {
  checkoutLabel,
  machineLabel,
  PANEL_LABEL,
  PAYMENT_METHOD,
  TAB_ITEM_ORIGIN,
  TAB_ITEM_STATUS,
  TAB_STATUS,
  TERMINAL_STATUS
} from './tabLabels'

export interface IEventTabDrawerProps {
  eventId: number | null
  checkinId: number | null
  isOpen: boolean
  onClose: () => void
}

const ITEM_BOX = { p: 3, borderRadius: 'lg', border: '1px', borderColor: 'gray.100', bg: 'gray.50' } as const

/**
 * `Lançado por Maquininha 01 · Terminal POS · 13/10/2026 21:04`. O dispositivo nunca some da linha:
 * quem lançou ou removeu é a pergunta desta tela, e "não se sabe" (lance anterior ao registro de
 * dispositivos) aparece como `-`. Só a data, quando falta, sai.
 */
function actorLine(machine: EventTabMachine | null, at: string | null, prefix?: string, byPanel = false): string {
  // The panel's check-out has no device (ABRA-425).
  const actor = byPanel ? PANEL_LABEL : machineLabel(machine)
  const device = prefix ? `${prefix} ${actor}` : actor
  return [device, formatDateTime(at)].filter(Boolean).join(' · ')
}

function Moment({ at, caption }: { at: string | null; caption: string }) {
  return (
    <>
      <EmptyCell value={formatDateTime(at)} />
      <Text as="span" variant="caption" display="block">
        {caption}
      </Text>
    </>
  )
}

export default function EventTabDrawer({ eventId, checkinId, isOpen, onClose }: IEventTabDrawerProps) {
  const toast = useAppToast()
  const tabsService = new EventTabsService()

  const [tab, setTab] = useState<EventTabDetail | null>(null)
  const [loadError, setLoadError] = useState(false)

  // Uma comanda aberta logo depois de outra não pode receber a resposta atrasada da primeira.
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (isOpen) loadTab()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, eventId, checkinId])

  function loadTab() {
    if (eventId === null || checkinId === null) return
    const reqId = ++requestIdRef.current
    setLoadError(false)
    tabsService
      .get(eventId, checkinId)
      .then((data) => {
        if (reqId !== requestIdRef.current) return
        setTab(data)
      })
      .catch((error) => {
        if (reqId !== requestIdRef.current) return
        setLoadError(true)
        toast.error('Erro ao carregar a comanda', error)
      })
  }

  // Só a comanda pedida: a anterior não pisca na tela enquanto a nova carrega. Reabrir a mesma
  // mostra o que já havia e atualiza por baixo.
  const current = tab && tab.id === checkinId ? tab : null

  // An item added by the panel's check-out has no device: its payment tells who added it. With no
  // payment (a tab closed at R$ 0,00) only the panel leaves a checkout item without a device.
  const panelPaymentIds = (current?.payments ?? []).filter((p) => p.origin === 'panel').map((p) => p.id)
  const addedByPanel = (item: EventTabItem) =>
    item.origin === 'checkout' &&
    (panelPaymentIds.includes(item.payment_id ?? 0) || (item.added_by === null && item.payment_id === null))

  const participantCaption = current?.participant
    ? [current.participant.name, current.participant.email, formatPhone(current.participant.phone)]
        .filter(Boolean)
        .join(' · ')
    : 'Sem participante'

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          {/* `h2` porque os blocos da gaveta são `SectionTitle variant="mini"`, que emite `h3`:
              sem este nível o outline pularia do `h1` da página direto para eles. */}
          <Heading as="h2" fontWeight="700">
            Comanda {current?.tab_code}
          </Heading>
          {current && (
            <Text variant="caption" mt={0.5}>
              {participantCaption}
            </Text>
          )}
        </DrawerHeader>

        <DrawerBody>
          {loadError ? (
            <EmptyState
              icon="lni lni-list"
              tone="error"
              title="Não foi possível carregar a comanda"
              action={<ActionButton action="refresh" label="Tentar novamente" showLabel mr={0} onClick={loadTab} />}
            />
          ) : !current ? (
            <LoadingBlock entity="comanda" />
          ) : (
            <VStack align="stretch" spacing={5} pt={2}>
              <Box>
                <SectionTitle variant="mini">Resumo</SectionTitle>
                <VStack align="stretch" spacing={1}>
                  <DetailField layout="inline" label="Consumido" value={formatCurrency(current.totals.consumed)} />
                  <DetailField layout="inline" label="Pago" value={formatCurrency(current.totals.paid)} />
                  <DetailField layout="inline" label="A cobrar" value={formatCurrency(current.totals.open)} />
                  <DetailField layout="inline" label="Removido" value={formatCurrency(current.totals.removed)} />
                  <DetailField layout="inline" label="Entrada">
                    <Moment at={current.checkin_at} caption={machineLabel(current.checkin_machine)} />
                  </DetailField>
                  <DetailField layout="inline" label="Saída">
                    {current.status === 'open' ? (
                      <StatusBadge status={current.status} statuses={TAB_STATUS} />
                    ) : (
                      <Moment at={current.checkout_at} caption={checkoutLabel(current)} />
                    )}
                  </DetailField>
                </VStack>
              </Box>

              <Box>
                <SectionTitle variant="mini" mb={3}>
                  Itens consumidos ({current.items.length})
                </SectionTitle>
                {current.items.length === 0 ? (
                  <Text variant="caption">Nenhum item consumido</Text>
                ) : (
                  <VStack align="stretch" spacing={3}>
                    {current.items.map((item) => (
                      <Box key={item.key} {...ITEM_BOX}>
                        <Flex justify="space-between" align="flex-start" gap={3}>
                          <Box minW={0}>
                            <Text fontWeight="700" color="gray.800">
                              {item.description}
                            </Text>
                            {item.detail && <Text variant="caption">{item.detail}</Text>}
                            {(item.attendees ?? []).length > 0 && (
                              <Text variant="caption">Inscritos: {item.attendees?.join(', ')}</Text>
                            )}
                          </Box>
                          <Box textAlign="right" flexShrink={0}>
                            <Text fontWeight="700" color="gray.800">
                              {formatCurrencyOrFree(item.total)}
                            </Text>
                            <Text variant="caption">
                              {item.quantity}× {formatCurrencyOrFree(item.unit_price)}
                            </Text>
                            {item.frame_amount > 0 && (
                              <Text variant="caption">+ moldura {formatCurrency(item.frame_amount)}</Text>
                            )}
                            {/* ABRA-338 — taxa sistêmica cobrada por cima do lance, já somada no total da obra. */}
                            {(item.system_fee_amount ?? 0) > 0 && (
                              <Text variant="caption">
                                + taxa sistêmica {formatCurrency(item.system_fee_amount ?? 0)}
                              </Text>
                            )}
                          </Box>
                        </Flex>
                        <Flex align="center" gap={2} mt={2} wrap="wrap">
                          <StatusBadge status={item.status} statuses={TAB_ITEM_STATUS} />
                          <Badge>{TAB_ITEM_ORIGIN[item.origin] ?? item.origin}</Badge>
                        </Flex>
                        <Text variant="caption" mt={1}>
                          {actorLine(item.added_by, item.added_at, 'Lançado por', addedByPanel(item))}
                        </Text>
                        {item.status === 'cancelled' && item.cancelled_by && (
                          <Text variant="caption">Cancelada por {item.cancelled_by}</Text>
                        )}
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>

              <Box>
                <SectionTitle variant="mini" mb={3}>
                  Removidos no checkout ({current.removals.length})
                </SectionTitle>
                {current.removals.length === 0 ? (
                  <Text variant="caption">Nenhum item removido no checkout</Text>
                ) : (
                  <VStack align="stretch" spacing={3}>
                    {current.removals.map((removal) => (
                      <Box key={removal.id} {...ITEM_BOX}>
                        <Flex justify="space-between" align="flex-start" gap={3}>
                          <Box minW={0}>
                            <Text fontWeight="700" color="gray.600" textDecoration="line-through">
                              {removal.description}
                            </Text>
                            {removal.detail && <Text variant="caption">{removal.detail}</Text>}
                          </Box>
                          <Box textAlign="right" flexShrink={0}>
                            <Text fontWeight="700" color={removal.reverted_at ? 'gray.500' : 'orange.600'}>
                              {formatCurrencyOrFree(removal.amount)}
                            </Text>
                            <Text variant="caption">
                              {removal.quantity}× {formatCurrencyOrFree(removal.unit_price)}
                            </Text>
                          </Box>
                        </Flex>
                        <Text variant="caption" mt={2}>
                          {actorLine(removal.removed_by, removal.removed_at, 'Removido por', removal.removed_by_panel)}
                        </Text>
                        {removal.reverted_at && (
                          <Flex align="center" gap={2} mt={2} wrap="wrap">
                            <Badge colorScheme="blue">Desfeito no estorno</Badge>
                            <Text variant="caption">{actorLine(removal.reverted_by, removal.reverted_at, 'por')}</Text>
                          </Flex>
                        )}
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>

              <Box>
                <SectionTitle variant="mini" mb={3}>
                  Pagamentos ({current.payments.length})
                </SectionTitle>
                {current.payments.length === 0 ? (
                  <Text variant="caption">Nenhum pagamento registrado</Text>
                ) : (
                  <VStack align="stretch" spacing={3}>
                    {current.payments.map((payment) => (
                      <Box key={payment.id} {...ITEM_BOX}>
                        <Flex justify="space-between" align="center" gap={3}>
                          <StatusBadge status={payment.method} statuses={PAYMENT_METHOD} />
                          <Text fontWeight="700" color="gray.800">
                            {formatCurrency(payment.amount_paid)}
                          </Text>
                        </Flex>
                        <Flex justify="space-between" align="center" gap={3} mt={2}>
                          <Text variant="caption">
                            {actorLine(payment.machine, payment.paid_at, undefined, payment.origin === 'panel')}
                          </Text>
                          <StatusBadge status={payment.status} statuses={TERMINAL_STATUS} />
                        </Flex>
                        {(payment.system_fee_amount ?? 0) > 0 && (
                          <Text variant="caption">
                            Inclui taxa sistêmica de {formatCurrency(payment.system_fee_amount ?? 0)}
                          </Text>
                        )}
                        {payment.voided_at && (
                          <Text variant="caption">Estornado em {formatDateTime(payment.voided_at)}</Text>
                        )}
                        {payment.payment_method_note && (
                          <Text variant="caption">Observação: {payment.payment_method_note}</Text>
                        )}
                        {payment.proof_image_url && (
                          <Box mt={2}>
                            <PaymentProof url={payment.proof_image_url} maxImageHeight={240} />
                          </Box>
                        )}
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}
